import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Button,
  ButtonGroup,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/TableContainer';
import { OrderNode } from '../../graphql/queries/orders';
import BookingStatusSelect from '../../components/Bookings/BookingStatusSelect';
import CustomerCancelRequestButton from '../../components/Orders/CustomerCancelRequestButton';
import { useScopedOrders } from '../../hooks/useScopedOrders';
import { formatOrderTotal } from '../../utils/orderLineItemMeta';

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function sortNewestFirst(a: OrderNode, b: OrderNode): number {
  const parseTime = (value?: string) => {
    if (!value) return -1;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : -1;
  };

  const parseOrderNumber = (value?: string) => {
    if (!value) return 0;
    const num = Number(String(value).replace(/[^\d]/g, ''));
    return Number.isFinite(num) ? num : 0;
  };

  const aTime = parseTime(a.date);
  const bTime = parseTime(b.date);
  if (aTime !== bTime) return bTime - aTime;

  const aOrder = parseOrderNumber(a.orderNumber);
  const bOrder = parseOrderNumber(b.orderNumber);
  if (aOrder !== bOrder) return bOrder - aOrder;

  return (b.databaseId || 0) - (a.databaseId || 0);
}

const OrdersList: React.FC = () => {
  document.title = 'Orders | NGK Tours';
  const navigate = useNavigate();
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { orders: fetchedOrders, loading, error, refetch } = useScopedOrders({ first: 100 });

  const statusMap: Record<string, string[]> = {
    'Processing': ['processing', 'PROCESSING'],
    'On hold': ['on-hold', 'on_hold', 'ON_HOLD'],
    'Completed': ['completed', 'COMPLETED'],
    'Cancel Request': ['cancel request', 'cancel-request', 'CANCEL REQUEST', 'cancelled'],
  };

  const orders = useMemo(() => {
    const nodes = [...fetchedOrders].sort(sortNewestFirst);
    if (!statusFilter) return nodes;
    const allowed = statusMap[statusFilter] || [statusFilter];
    return nodes.filter((o) => {
      const s = (o.status || '').toLowerCase().replace(/-/g, ' ');
      return allowed.some((a) => a.toLowerCase().replace(/-/g, ' ') === s || s.includes(a.toLowerCase()));
    });
  }, [fetchedOrders, statusFilter]);

  const columns = useMemo(
    () => [
      {
        Header: 'Order #',
        accessor: 'orderNumber',
        width: '12%',
        Cell: (cellProps: any) => {
          const row = cellProps.row.original as OrderNode;
          const customerName =
            row.customer?.firstName || row.customer?.lastName
              ? [row.customer?.firstName, row.customer?.lastName].filter(Boolean).join(' ')
              : row.billing?.firstName || row.billing?.lastName
                ? [row.billing?.firstName, row.billing?.lastName].filter(Boolean).join(' ')
                : row.customer?.email || row.billing?.email || '—';
          return (
            <div>
              <Link to={`/orders/${row.databaseId}`} className="fw-medium text-primary">
                #{row.orderNumber ?? row.databaseId}
              </Link>
              <div className="text-muted small">{customerName}</div>
            </div>
          );
        },
      },
      {
        Header: 'Date',
        accessor: 'date',
        width: '14%',
        Cell: (cellProps: any) => formatDate(cellProps.row.original.date),
      },
      {
        Header: 'Status',
        accessor: 'status',
        width: '14%',
        Cell: (cellProps: any) => {
          const row = cellProps.row.original as OrderNode;
          return (
            <BookingStatusSelect orderId={row.databaseId} status={row.status} />
          );
        },
      },
      {
        Header: 'Total',
        id: 'total',
        accessor: (row: OrderNode) => formatOrderTotal(row.total, row.currency),
        width: '14%',
      },
      {
        Header: 'Origin',
        accessor: 'origin',
        width: '10%',
        Cell: () => 'Direct',
      },
      {
        Header: 'Action',
        accessor: 'action',
        width: '10%',
        Cell: (cellProps: any) => {
          const row = cellProps.row.original as OrderNode;
          return (
            <div className="d-flex flex-wrap align-items-center gap-1">
              <Button
                color="primary"
                size="sm"
                className="btn-sm"
                onClick={() => navigate(`/orders/${row.databaseId}`)}
              >
                View
              </Button>
              <CustomerCancelRequestButton
                orderId={row.databaseId}
                status={row.status}
                size="sm"
              />
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Orders" breadcrumbItem="Orders List" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={8}>
                      <ButtonGroup>
                        <Button
                          color={!statusFilter ? 'primary' : 'light'}
                          size="sm"
                          onClick={() => setStatusFilter('')}
                        >
                          All
                        </Button>
                        {['Processing', 'On hold', 'Completed', 'Cancel Request'].map((s) => (
                          <Button
                            key={s}
                            color={statusFilter === s ? 'primary' : 'light'}
                            size="sm"
                            onClick={() => setStatusFilter(s)}
                          >
                            {s}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </Col>
                    <Col sm={4} className="text-end">
                      <Button color="secondary" size="sm" outline onClick={() => refetch()}>
                        Refresh
                      </Button>
                    </Col>
                  </Row>
                  {error && (
                    <Alert color="danger" className="mb-3">
                      {(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]?.message || error.message}
                    </Alert>
                  )}
                  {loading && !fetchedOrders.length ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading orders...</p>
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={orders}
                      isGlobalFilter={true}
                      customPageSize={pageSize}
                      className="align-middle table-nowrap"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OrdersList;
